/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';

// Component imports
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import { Col, Empty, Form, Popover, Row, Select, Spin } from 'antd';
import LLMDynamicModelConfigFields from './LLMDynamicModelConfigFields';
import { SettingOutlined } from '@ant-design/icons';

// Misc imports
import { INTEGRATION_TYPE } from '../../../../constants/integrations';
import { validatePromptJson } from '../../../../utils/misc/validatePromptJson';
import { useGetModelMappings } from '../../../../utils/hooks/useGetLLMModelMappings';
import { useLLMFormFieldsValidation } from '../../../../utils/hooks/useLLMFormFieldsValidation';
import { RICH_TEXT_TOOLBAR_ACTIONS } from '../../../../constants/markdown';
import { LLM_RESPONSE_FORMAT } from '../../../../constants/llmConfiguration';

/**
 * @typedef {object} LLMConfigurationFieldGroupProps
 * @property {object} form - The main form which this component is connected to
 * @property {string} integrationProviderType - The provider type required for the component to fetch models (e.g OPEN_AI_LLM)
 * @property {function} setCanSubmit - Used for validation to allow form save or preventing from save
 * @property {array} basePath - Required to connect the form fields with the main form and for setting up watchers
 * @property {boolean} shouldValidatePromptJson - Optional to validate or not prompt for containing json if json is enabled
 */

/**
 * =====================================================================================================
 *
 *
 * TECHNICAL SPECIFICATIONS
 * @param {LLMConfigurationFieldGroupProps} props - The props for the component
 * @returns {React.ReactElement} The rendered component.
 *
 *
 * BUSINESS CONTEXT
 * @decision - URL reference for the RFC responsible for this component https://wiki.helvia.io/books/helvia-rfcs/page/llm-unification
 *
 *
 * METADATA
 * @since 2026-02-16
 * @version 1.0.0
 * =====================================================================================================
 */

const LLMConfigurationFieldGroup = ({
  form,
  integrationProviderType,
  setCanSubmit = () => {},
  basePath,
  shouldValidatePromptJson = false
}) => {

  // We use `basePath?.join('.')` as the dependency. Strings are compared by value,
  // so `['a','b']` -> "a.b" remains stable even if the array reference changes.
  const configPath = useMemo(() => {
    const base = Array.isArray(basePath) ? basePath : (basePath ? [basePath] : ['data', 'generationParams']);
    return [...base, 'configuration'];
  }, [basePath ? basePath.join('.') : 'default']);

  const responseTypeOptions = useMemo(() => {
    return Object.values(LLM_RESPONSE_FORMAT);
  }, []);

  // useWatch crashes if you pass `[...configPath, 'model']` inline because it sees a new array ref.
  const modelPath = useMemo(() => [...configPath, 'model'], [configPath]);
  const optionsPath = useMemo(() => [...configPath, 'options'], [configPath]);
  const promptPath = useMemo(() => [...configPath, 'prompt'], [configPath]);
  const responseTypePath = useMemo(() => [...configPath, 'responseType'], [configPath]);

  const selectedModel = Form.useWatch(modelPath, form);
  const configValues = Form.useWatch(configPath, form);
  const responseTypeValue = Form.useWatch(responseTypePath, form);

  const prevProviderRef = useRef(integrationProviderType);
  const prevModelRef = useRef(selectedModel);

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [hasOptionsError, setHasOptionsError] = useState(false);

  const isResponseInJson = useMemo(() => {
    return responseTypeValue === LLM_RESPONSE_FORMAT.JSON.value;
  }, [responseTypeValue]);

  // Get the the model options to display and the mappings for the selected model
  const {
    mappings,
    modelOptions,
    isLoadingModels
  } = useGetModelMappings(integrationProviderType, selectedModel);

  // Validate both the model and prompt along with internal mappings validation
  useLLMFormFieldsValidation({
    form,
    configValues,
    configPath,
    optionsPath,
    setCanSubmit,
    setHasOptionsError
  });

  const handleOpenChange = (value) => {
    if (value && !selectedModel) {
      return;
    }
    setIsPopoverOpen(value);
  };

  // Reset model selection when changing provider
  useEffect(() => {
    if (prevProviderRef.current && prevProviderRef.current !== integrationProviderType) {
      form.setFields([
        { name: [...configPath, 'model'], value: null },
        { name: [...optionsPath], value: {} }
      ]);
    }

    prevProviderRef.current = integrationProviderType;
  }, [integrationProviderType, form]);

  // Set the mappings field to their default value when changing model
  useEffect(() => {
    if (!selectedModel) {
      prevModelRef.current = selectedModel;
      return;
    }

    if (prevModelRef.current && prevModelRef.current !== selectedModel) {
      const defaultValues = {};

      if (mappings && mappings.length > 0) {
        mappings.forEach((field) => {
          defaultValues[field.association.key] = field.defaultValue ?? null;
        });
      }

      form.setFieldValue([...optionsPath], defaultValues);
    }

    prevModelRef.current = selectedModel;
  }, [selectedModel, form, mappings, optionsPath]);

  const isAzureIntegration = useMemo(() => {
    return integrationProviderType === INTEGRATION_TYPE.AZURE_AI_LLM;
  }, [integrationProviderType]);

  return (
    <Row className="w-100">
      <Col xs={24}>
        <div className="d-flex align-items-start gap-2">
          <Form.Item
            name={modelPath}
            label={isAzureIntegration ? t('Deployment') : t('Model')}
            labelAlign="left"
            required
            rules={[{ required: true, message: t('Model cannot be blank.') }]}
            className="mb-0 w-50"
          >
            <Select
              showSearch
              placeholder={isAzureIntegration ? t('Type / Select deployment') : t('Type / Select model')}
              getPopupContainer={triggerNode => triggerNode.parentElement}
              loading={isLoadingModels}
              disabled={!integrationProviderType || isLoadingModels}
              notFoundContent={isLoadingModels ? <Spin size="small" /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
              options={modelOptions}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item label=" ">
            <Popover
              className="border-0 mb-0"
              placement="bottomLeft"
              trigger="click"
              destroyTooltipOnHide={false}
              open={isPopoverOpen}
              showArrow={false}
              overlayInnerStyle={{ width: 325 }}
              overlayClassName="model-configuration-field-group-popover"
              getPopupContainer={triggerNode => triggerNode.parentElement}
              onOpenChange={handleOpenChange}
              content={
                <LLMDynamicModelConfigFields
                  basePath={optionsPath}
                  modelMappings={mappings || []}
                  form={form}
                />
              }
            >
              <SettingOutlined
                className={`
              ${!selectedModel ? 'cursor-not-allowed' : ''} 
              ${isPopoverOpen && !hasOptionsError ? 'text-primary' : ''} 
              ${hasOptionsError ? 'text-error' : ''}
              `}
              />
            </Popover>
          </Form.Item>
        </div>
      </Col>
      <Col xs={24}>
        <Form.Item
          label={t('Prompt')}
          required
          className="mb-4"
          name={promptPath}
          validateFirst={true}
          rules={[
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.reject(new Error(t('Prompt cannot be blank.')));
                }

                const cleanText = value
                  .replace(/<[^>]*>/g, '')
                  .replace(/&[^;]+;/g, '')
                  .replace(/[\u200B-\u200D\uFEFF]/g, '')
                  .replace(/\s+/g, '')
                  .trim();

                if (cleanText.length === 0) {
                  return Promise.reject(new Error(t('Prompt cannot be blank.')));
                }

                return Promise.resolve();
              }
            },
            {
              validator: (_, value) => {
                if(!shouldValidatePromptJson) {
                  return;
                }
                return validatePromptJson(value, isResponseInJson);
              }
            }
          ]}
        >
          <RichTextEditor
            toolbarActions={[
              RICH_TEXT_TOOLBAR_ACTIONS.HEADING,
              RICH_TEXT_TOOLBAR_ACTIONS.BOLD,
              RICH_TEXT_TOOLBAR_ACTIONS.ITALIC,
              RICH_TEXT_TOOLBAR_ACTIONS.BULLET_LIST,
              RICH_TEXT_TOOLBAR_ACTIONS.ORDERED_LIST,
              RICH_TEXT_TOOLBAR_ACTIONS.CUSTOM_VARIABLE
            ]}
            copySourceButton
            placeholder={t('Enter the prompt')}
            elementStyles={{ p: { marginBottom: 0 } }}
          />
        </Form.Item>
      </Col>
      <Col xs={24}>
        <Form.Item
          label={'Response Format'}
          name={responseTypePath}
          className="mb-4"
        >
          <Select
            defaultValue={LLM_RESPONSE_FORMAT.TEXT.value}
            options={responseTypeOptions}
            getPopupContainer={triggerNode => triggerNode.parentElement}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

LLMConfigurationFieldGroup.propTypes = {
  form: PropTypes.any.isRequired,
  integrationProviderType: PropTypes.string,
  setCanSubmit: PropTypes.func,
  basePath: PropTypes.arrayOf(PropTypes.string).isRequired,
  shouldValidatePromptJson: PropTypes.bool
};

export default LLMConfigurationFieldGroup;
